import React from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Bell, MessageSquare, MessageCircle, Phone, PhoneIncoming, PhoneMissed,
  Calendar, AlertTriangle, CheckCheck, BellOff, Mail, UserPlus, Hand,
  Loader2,
} from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { useNotifications, Notification } from '@/hooks/useNotifications'
import { useUserTimezone } from '@/hooks/useUserTimezone'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { EmptyState, GradientAvatar } from '@/components/dashboard-ui'
import { cn } from '@/lib/utils'

type Tone = 'royal' | 'periwinkle' | 'violet' | 'amber' | 'green' | 'rose' | 'neutral'

// Restrained chip backgrounds — barely-there hint of color so the icon
// reads, not the chip. Dot color = saturated version for the unread cue.
const TONE_CHIP: Record<Tone, { bg: string; icon: string; dot: string }> = {
  royal:      { bg: 'bg-brand-royal/8',        icon: 'text-brand-royal',                       dot: 'bg-brand-royal' },
  periwinkle: { bg: 'bg-brand-periwinkle/8',   icon: 'text-brand-periwinkle',                  dot: 'bg-brand-periwinkle' },
  violet:     { bg: 'bg-violet-500/8',         icon: 'text-violet-500',                        dot: 'bg-violet-500' },
  amber:      { bg: 'bg-amber-500/8',          icon: 'text-amber-600 dark:text-amber-400',    dot: 'bg-amber-500' },
  green:      { bg: 'bg-emerald-500/8',        icon: 'text-emerald-600 dark:text-emerald-400', dot: 'bg-emerald-500' },
  rose:       { bg: 'bg-rose-500/8',           icon: 'text-rose-500',                          dot: 'bg-rose-500' },
  neutral:    { bg: 'bg-muted',                icon: 'text-muted-foreground',                  dot: 'bg-muted-foreground/60' },
}

const typeConfig: Record<string, { icon: typeof Bell; tone: Tone }> = {
  new_chat:            { icon: MessageSquare, tone: 'periwinkle' },
  chat_assigned:       { icon: MessageSquare, tone: 'green' },
  chat_waiting:        { icon: MessageSquare, tone: 'amber' },
  chat_completed:      { icon: MessageSquare, tone: 'neutral' },
  human_request:       { icon: Hand,          tone: 'amber' },
  call_incoming:       { icon: PhoneIncoming, tone: 'green' },
  call_completed:      { icon: Phone,         tone: 'royal' },
  call_missed:         { icon: PhoneMissed,   tone: 'rose' },
  sms_incoming:        { icon: MessageCircle, tone: 'violet' },
  booking_made:        { icon: Calendar,      tone: 'green' },
  booking_reminder:    { icon: Calendar,      tone: 'amber' },
  lead_created:        { icon: UserPlus,      tone: 'royal' },
  usage_limit_warning: { icon: AlertTriangle, tone: 'amber' },
  usage_limit_reached: { icon: AlertTriangle, tone: 'rose' },
  system_alert:        { icon: AlertTriangle, tone: 'amber' },
  team_message:        { icon: Mail,          tone: 'periwinkle' },
  affiliate_email_received: { icon: Mail,     tone: 'royal' },
}

/** Notification types where the actor is a person — render as a gradient
 *  avatar instead of an icon chip for instant visual identity. */
const PERSON_TYPES = new Set(['team_message'])

function getNotificationRoute(notification: Notification): string | null {
  const data = notification.data || {}
  switch (notification.type) {
    case 'new_chat':
    case 'chat_assigned':
    case 'chat_waiting':
      return data.sessionId ? `/chats?sessionId=${data.sessionId}` : '/chats'
    case 'chat_completed':
      return data.sessionId ? `/conversations?id=${data.sessionId}` : '/conversations'
    case 'call_incoming':
    case 'call_completed':
    case 'call_missed':
      return '/calls'
    case 'sms_incoming':
      return data.conversationId ? `/sms?id=${data.conversationId}` : '/sms'
    case 'booking_made':
    case 'booking_reminder':
      // Deep-link to the specific booking when we have an id. Without it,
      // the user lands on the calendar with nothing focused — they have to
      // hunt for the booking they were just notified about.
      return data.bookingId ? `/bookings?bookingId=${encodeURIComponent(data.bookingId)}` : '/bookings'
    case 'lead_created':
      return '/leads'
    case 'team_message':
      return data.senderId ? `/messages?contact=${data.senderId}` : '/messages'
    case 'affiliate_email_received':
      // Optional ?thread=<id> opens the dropdown's target thread directly;
      // the email page reads this and selects the thread on mount.
      return data.threadId
        ? `/affiliate/email?thread=${encodeURIComponent(data.threadId)}`
        : '/affiliate/email'
    default:
      return null
  }
}

function NotificationItem({
  notification,
  onNavigate,
  onMarkRead,
}: {
  notification: Notification
  onNavigate: (path: string) => void
  onMarkRead: (id: string) => void
}) {
  const userTz = useUserTimezone()
  const config = typeConfig[notification.type] || { icon: Bell, tone: 'neutral' as Tone }
  const Icon = config.icon
  const tone = TONE_CHIP[config.tone]
  const isUnread = !notification.readAt
  const route = getNotificationRoute(notification)
  const data = (notification.data || {}) as any
  const isPerson = PERSON_TYPES.has(notification.type) && data.senderName

  // For booking reminders, the server bakes the time in the firm's TZ.
  // Re-render in this user's TZ so a CST staff member viewing a PST firm's
  // 2pm booking sees "4:00 PM" (their time), not "2:00 PM" (firm time).
  let displayBody = notification.body
  if (notification.type === 'booking_reminder' && data?.appointmentTime) {
    try {
      const apptDate = new Date(data.appointmentTime)
      const userTime = apptDate.toLocaleTimeString('en-US', {
        timeZone: userTz, hour: 'numeric', minute: '2-digit', hour12: true,
      })
      const itemLabel = data?.bookingType === 'task' ? 'Task' : 'Appointment'
      const phone = data?.phone
      displayBody = `${itemLabel} ${userTime}.${phone ? ` Call ${phone}` : ''}`.trim()
    } catch { /* fall back to server-rendered body */ }
  }

  const handleClick = () => {
    if (isUnread) onMarkRead(notification.id)
    if (route) onNavigate(route)
  }

  return (
    <button
      onClick={handleClick}
      className={cn(
        'group relative w-full text-left px-4 py-3 flex items-start gap-3',
        'transition-[background-color] duration-200 [transition-timing-function:var(--ease-out,cubic-bezier(0.23,1,0.32,1))]',
        'hover:bg-muted/40 focus-visible:bg-muted/40 focus-visible:outline-none',
      )}
    >
      {/* Icon chip OR gradient avatar */}
      {isPerson ? (
        <GradientAvatar
          seed={data.senderId || data.senderEmail || data.senderName}
          name={data.senderName}
          src={data.senderAvatar}
          size={36}
        />
      ) : (
        <span className={cn('h-9 w-9 rounded-xl flex items-center justify-center shrink-0', tone.bg)}>
          <Icon className={cn('h-4 w-4', tone.icon)} strokeWidth={1.75} />
        </span>
      )}

      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            {/* Inline unread dot — Linear style, restrained */}
            {isUnread && (
              <span aria-hidden="true" className={cn('h-1.5 w-1.5 rounded-full shrink-0', tone.dot)} />
            )}
            <p className={cn(
              'text-sm leading-tight truncate',
              isUnread ? 'font-semibold text-foreground' : 'text-foreground/90',
            )}>
              {notification.title}
            </p>
          </div>
          <span className="text-[10px] tabular-nums text-muted-foreground/60 shrink-0 whitespace-nowrap">
            {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: false })}
          </span>
        </div>
        {displayBody && (
          <p className={cn(
            'text-xs mt-1 leading-snug line-clamp-2',
            isUnread ? 'text-foreground/70' : 'text-muted-foreground/80',
          )}>
            {displayBody}
          </p>
        )}
      </div>
    </button>
  )
}

export default function NotificationDropdown() {
  const navigate = useNavigate()
  const { notifications, unreadCount, loading, error, markAsRead, markAllAsRead } = useNotifications()
  const [open, setOpen] = React.useState(false)

  const handleNavigate = (path: string) => {
    setOpen(false)
    navigate(path)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-9 w-9 press-scale"
          aria-label={unreadCount > 0 ? `Notifications (${unreadCount} unread)` : 'Notifications'}
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <>
              {/* Soft pulsing halo behind the badge */}
              <span
                aria-hidden="true"
                className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-brand-periwinkle/40 blur-[6px] animate-breath"
              />
              <span
                className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 flex items-center justify-center rounded-full bg-gradient-to-br from-brand-royal to-brand-periwinkle text-white text-[10px] font-bold border-2 border-background shadow-sm shadow-brand-periwinkle/40 tabular-nums"
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={10}
        className="w-[360px] p-0 overflow-hidden rounded-2xl border bg-card shadow-tinted-lg md:bg-card/95 md:backdrop-blur-xl"
      >
        {/* Header — title + count pill + mark-all. No eyebrow: the
            popover is already small + focused, no need for chrome
            inside chrome. */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border/40">
          <div className="flex items-center gap-2 min-w-0">
            <h3 className="font-heading font-semibold text-base tracking-tight">Notifications</h3>
            {unreadCount > 0 && (
              <span className="inline-flex items-center justify-center min-w-[20px] h-[18px] px-1.5 rounded-full bg-brand-periwinkle/15 text-brand-periwinkle text-[10px] font-bold tabular-nums">
                {unreadCount}
              </span>
            )}
          </div>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={markAllAsRead}
              className="h-7 text-xs text-muted-foreground hover:text-foreground press-scale"
            >
              <CheckCheck className="h-3.5 w-3.5 mr-1" />
              Mark all read
            </Button>
          )}
        </div>

        {/* Notification list */}
        <ScrollArea className="max-h-[440px]">
          {loading ? (
            <div className="py-10 text-center">
              <Loader2 className="h-5 w-5 animate-spin text-brand-periwinkle mx-auto" />
              <p className="text-xs text-muted-foreground mt-3">Loading…</p>
            </div>
          ) : error ? (
            <div className="p-4">
              <EmptyState
                icon={AlertTriangle}
                tone="rose"
                compact
                title="Couldn't load notifications"
                description={error}
              />
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-4">
              <EmptyState
                icon={BellOff}
                tone="periwinkle"
                compact
                title="All caught up"
                description="New activity from calls, chats, bookings, and your team will land here in real time."
              />
            </div>
          ) : (
            <div className="divide-y divide-border/40">
              {notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onNavigate={handleNavigate}
                  onMarkRead={markAsRead}
                />
              ))}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  )
}
